import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["isDisabled", "isReadOnly", "sessionBackup", "getApi"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { ArrayField, useField, useFieldApi, useFieldState, useFormApi, useFormState, useMultistepApi, Form as Informed, Multistep } from 'informed';
import { debounce } from 'lodash-es';
import { invokeAll, isDefined, safeInvoke } from '../../../../library';
import { Field } from '../../field';
import { Controls, Control } from '../../control';
import { Input } from '../../text-input';
import { PasswordInput } from '../../password-input';
import { TextArea } from '../../textarea';
import { Select } from '../../select';
import { Checkbox, Switch } from '../../choice';
import { RadioGroup } from '../../choice';
import { HiddenInput } from '../../hidden-input';
import { RichText } from '../../rich-text';
import { NumberInput } from '../../number-input';
import { DateInput } from '../../date-input';
import { TimeInput } from '../../time-input';
var CustomFormContext = React.createContext({
  isDisabled: false,
  isReadOnly: false
});
var reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;
export var Form = function Form(props) {
  var _props$isDisabled = props.isDisabled,
    isDisabled = _props$isDisabled === void 0 ? false : _props$isDisabled,
    _props$isReadOnly = props.isReadOnly,
    isReadOnly = _props$isReadOnly === void 0 ? false : _props$isReadOnly,
    sessionBackup = props.sessionBackup,
    getApi = props.getApi,
    passProps = _objectWithoutProperties(props, _excluded);
  var _React$useState = React.useState(),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    api = _React$useState2[0],
    setApi = _React$useState2[1];
  var sessionKey = "@core-ui/form/".concat(sessionBackup, "__bu");
  function reviveDates(key, value) {
    if (typeof value === 'string') {
      var a = reISO.exec(value);
      if (a) {
        return new Date(value);
      }
    }
    return value;
  }
  React.useEffect(function () {
    if (isDefined(sessionBackup) && api) {
      var rawState = sessionStorage.getItem(sessionKey);
      var values = rawState ? JSON.parse(rawState, reviveDates) : null;
      if (values) {
        api.setValues(values);
      }
    }
  }, [api, sessionBackup]);
  var debouncedSave = debounce(function (key, values) {
    sessionStorage.setItem(key, JSON.stringify(values));
  }, 100);
  function handleChange(state) {
    if (isDefined(props.sessionBackup)) {
      debouncedSave(sessionKey, state.values);
    }
  }
  return React.createElement(CustomFormContext.Provider, {
    value: {
      isDisabled: isDisabled,
      isReadOnly: isReadOnly
    }
  }, React.createElement(Informed, _extends({}, passProps, {
    getApi: invokeAll(setApi, getApi),
    onChange: invokeAll(handleChange, props.onChange)
  })));
};
Form.Multistep = Multistep;
Form.Array = ArrayField;
Form.Step = Multistep.Step;
Form.Field = Field;
Form.Controls = Controls;
Form.Control = Control;
var FieldSetContext = React.createContext({
  isDisabled: false,
  isReadOnly: false
});
Form.FieldSet = function (props) {
  var _props$isDisabled2 = props.isDisabled,
    isDisabled = _props$isDisabled2 === void 0 ? false : _props$isDisabled2,
    _props$isReadOnly2 = props.isReadOnly,
    isReadOnly = _props$isReadOnly2 === void 0 ? false : _props$isReadOnly2,
    children = props.children;
  return React.createElement("fieldset", {
    disabled: isDisabled
  }, React.createElement(FieldSetContext.Provider, {
    value: {
      isDisabled: isDisabled,
      isReadOnly: isReadOnly
    }
  }, children));
};
Form.Input = function (props) {
  var _fieldState$value;
  var _React$useContext = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext.isDisabled,
    fieldReadonly = _React$useContext.isReadOnly;
  var _React$useContext2 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext2.isDisabled,
    formReadonly = _React$useContext2.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.readOnly;
  var _useField = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      readOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField.fieldState,
    fieldApi = _useField.fieldApi,
    ref = _useField.ref,
    render = _useField.render,
    userProps = _useField.userProps;
  var handleUpdate = function handleUpdate(field, value, e) {
    fieldApi.setValue(value);
    safeInvoke(userProps.onUpdate, field, value, e);
  };
  var handleBlur = function handleBlur(e) {
    fieldApi.setTouched(true);
    safeInvoke(userProps.onBlur, e);
  };
  return render(React.createElement(Input, _extends({}, userProps, {
    type: props.type,
    name: props.name || props.field,
    value: (_fieldState$value = fieldState.value) !== null && _fieldState$value !== void 0 ? _fieldState$value : '',
    onUpdate: handleUpdate,
    onBlur: handleBlur,
    ref: ref
  })));
};
Form.Number = function (props) {
  var _fieldState$value2;
  var _React$useContext3 = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext3.isDisabled,
    fieldReadonly = _React$useContext3.isReadOnly;
  var _React$useContext4 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext4.isDisabled,
    formReadonly = _React$useContext4.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.readOnly;
  var _useField2 = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      readOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField2.fieldState,
    fieldApi = _useField2.fieldApi,
    ref = _useField2.ref,
    render = _useField2.render,
    userProps = _useField2.userProps;
  var handleUpdate = function handleUpdate(field, value, e) {
    fieldApi.setValue(value);
    safeInvoke(userProps.onChange, e);
  };
  var handleBlur = function handleBlur(e) {
    fieldApi.setTouched(true);
    safeInvoke(userProps.onBlur, e);
  };
  return render(React.createElement(NumberInput, _extends({}, userProps, {
    name: props.name || props.field,
    value: (_fieldState$value2 = fieldState.value) !== null && _fieldState$value2 !== void 0 ? _fieldState$value2 : '',
    onUpdate: handleUpdate,
    onBlur: handleBlur
  })));
};
Form.Password = function (props) {
  var _React$useContext5 = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext5.isDisabled,
    fieldReadonly = _React$useContext5.isReadOnly;
  var _React$useContext6 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext6.isDisabled,
    formReadonly = _React$useContext6.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.readOnly;
  var _useField3 = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      readOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField3.fieldState,
    fieldApi = _useField3.fieldApi,
    ref = _useField3.ref,
    render = _useField3.render,
    userProps = _useField3.userProps;
  var handleUpdate = function handleUpdate(field, value, e) {
    fieldApi.setValue(value);
    safeInvoke(userProps.onUpdate, field, value, e);
  };
  var handleBlur = function handleBlur(e) {
    fieldApi.setTouched(true);
    safeInvoke(userProps.onBlur, e);
  };
  return render(React.createElement(PasswordInput, _extends({}, userProps, {
    name: props.name || props.field,
    value: !!fieldState.value ? fieldState.value : '',
    onUpdate: handleUpdate,
    onBlur: handleBlur,
    ref: ref
  })));
};
Form.TextArea = function (props) {
  var _React$useContext7 = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext7.isDisabled,
    fieldReadonly = _React$useContext7.isReadOnly;
  var _React$useContext8 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext8.isDisabled,
    formReadonly = _React$useContext8.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.readOnly;
  var _useField4 = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      readOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField4.fieldState,
    fieldApi = _useField4.fieldApi,
    ref = _useField4.ref,
    render = _useField4.render,
    userProps = _useField4.userProps;
  var handleChange = function handleChange(e) {
    var value = e.target.value;
    fieldApi.setValue(value);
    safeInvoke(userProps.onChange, e);
  };
  var handleBlur = function handleBlur(e) {
    fieldApi.setTouched(true);
    safeInvoke(userProps.onBlur, e);
  };
  return render(React.createElement(TextArea, _extends({}, userProps, {
    value: fieldState.value,
    onChange: handleChange,
    onBlur: handleBlur,
    ref: ref
  })));
};
Form.RichText = function (props) {
  var _React$useContext9 = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext9.isDisabled,
    fieldReadonly = _React$useContext9.isReadOnly;
  var _React$useContext10 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext10.isDisabled,
    formReadonly = _React$useContext10.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.isReadOnly;
  var _useField5 = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      isReadOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField5.fieldState,
    fieldApi = _useField5.fieldApi,
    ref = _useField5.ref,
    render = _useField5.render,
    userProps = _useField5.userProps;
  var handleEditorStateChange = function handleEditorStateChange(editor) {
    fieldApi.setValue(_objectSpread(_objectSpread({}, fieldState.value), {}, {
      editor: editor
    }));
  };
  var handleContentStateChange = function handleContentStateChange(content) {
    fieldApi.setValue(_objectSpread(_objectSpread({}, fieldState.value), {}, {
      content: content
    }));
  };
  return render(React.createElement(RichText, _extends({
    editorState: fieldState.value ? fieldState.value.editor : undefined
  }, userProps, {
    onEditorStateChange: handleEditorStateChange,
    onContentStateChange: handleContentStateChange
  })));
};
Form.Checkbox = function (props) {
  var _React$useContext11 = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext11.isDisabled,
    fieldReadonly = _React$useContext11.isReadOnly;
  var _React$useContext12 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext12.isDisabled,
    formReadonly = _React$useContext12.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.readOnly || props.isReadOnly;
  var _useField6 = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      isReadOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField6.fieldState,
    fieldApi = _useField6.fieldApi,
    ref = _useField6.ref,
    render = _useField6.render,
    userProps = _useField6.userProps;
  var handleChange = function handleChange(e) {
    var value = e.target.checked;
    fieldApi.setValue(value);
    safeInvoke(props.onChange, e);
  };
  var handleBlur = function handleBlur(e) {
    fieldApi.setTouched(true);
    safeInvoke(props.onBlur, e);
  };
  return render(React.createElement(Checkbox, _extends({
    isChecked: fieldState.value !== undefined ? fieldState.value : false
  }, userProps, {
    onChange: handleChange,
    onBlur: handleBlur
  })));
};
Form.Switch = function (props) {
  var _React$useContext13 = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext13.isDisabled,
    fieldReadonly = _React$useContext13.isReadOnly;
  var _React$useContext14 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext14.isDisabled,
    formReadonly = _React$useContext14.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.readOnly || props.isReadOnly;
  var _useField7 = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      isReadOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField7.fieldState,
    fieldApi = _useField7.fieldApi,
    ref = _useField7.ref,
    render = _useField7.render,
    userProps = _useField7.userProps;
  var handleChange = function handleChange(e) {
    var value = e.target.checked;
    fieldApi.setValue(value);
    safeInvoke(props.onChange, e);
  };
  var handleBlur = function handleBlur(e) {
    fieldApi.setTouched(true);
    safeInvoke(props.onBlur, e);
  };
  return render(React.createElement(Switch, _extends({
    isChecked: fieldState.value !== undefined ? fieldState.value : false
  }, userProps, {
    field: props.field,
    onChange: handleChange,
    onBlur: handleBlur
  })));
};
Form.RadioGroup = function (props) {
  var _React$useContext15 = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext15.isDisabled,
    fieldReadonly = _React$useContext15.isReadOnly;
  var _React$useContext16 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext16.isDisabled,
    formReadonly = _React$useContext16.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.readOnly || props.isReadOnly;
  var _useField8 = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      isReadOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField8.fieldState,
    fieldApi = _useField8.fieldApi,
    ref = _useField8.ref,
    render = _useField8.render,
    userProps = _useField8.userProps;
  var handleChange = function handleChange(field, value, e) {
    fieldApi.setValue(value);
    safeInvoke(props.onUpdate, field, value, e);
  };
  return render(React.createElement(RadioGroup, _extends({
    value: fieldState.value
  }, userProps, {
    field: props.field,
    onUpdate: handleChange
  })));
};
Form.Select = function formSelect(props) {
  var _React$useContext17 = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext17.isDisabled,
    fieldReadonly = _React$useContext17.isReadOnly;
  var _React$useContext18 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext18.isDisabled,
    formReadonly = _React$useContext18.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.readOnly || props.isReadOnly;
  var _useField9 = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      isReadOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField9.fieldState,
    fieldApi = _useField9.fieldApi,
    ref = _useField9.ref,
    render = _useField9.render,
    userProps = _useField9.userProps;
  var handleUpdate = function handleUpdate(field, options) {
    fieldApi.setValue(options);
    safeInvoke(props.onUpdate, field, options);
  };
  var handleBlur = function handleBlur(e) {
    fieldApi.setTouched(true);
    safeInvoke(props.onBlur, e);
  };
  return render(React.createElement(Select, _extends({}, userProps, {
    activeOptions: fieldState.value ? fieldState.value : [],
    onUpdate: handleUpdate,
    onBlur: handleBlur
  })));
};
Form.Date = function (props) {
  var _fieldState$value3, _fieldState$value4;
  var _React$useContext19 = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext19.isDisabled,
    fieldReadonly = _React$useContext19.isReadOnly;
  var _React$useContext20 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext20.isDisabled,
    formReadonly = _React$useContext20.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.readOnly || props.isReadOnly;
  var _useField10 = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      isReadOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField10.fieldState,
    fieldApi = _useField10.fieldApi,
    ref = _useField10.ref,
    render = _useField10.render,
    userProps = _useField10.userProps;
  var handleChange = function handleChange(field, value, e) {
    fieldApi.setValue(value);
    safeInvoke(props.onUpdate, field, value, e);
  };
  var handleBlur = function handleBlur(e) {
    fieldApi.setTouched(true);
    safeInvoke(props.onBlur, e);
  };
  return render(React.createElement(DateInput, _extends({}, userProps, {
    startDate: (_fieldState$value3 = fieldState.value) === null || _fieldState$value3 === void 0 ? void 0 : _fieldState$value3.startDate,
    endDate: (_fieldState$value4 = fieldState.value) === null || _fieldState$value4 === void 0 ? void 0 : _fieldState$value4.endDate,
    onUpdate: handleChange,
    onBlur: handleBlur
  })));
};
Form.Time = function (props) {
  var _React$useContext21 = React.useContext(FieldSetContext),
    fieldDisabled = _React$useContext21.isDisabled,
    fieldReadonly = _React$useContext21.isReadOnly;
  var _React$useContext22 = React.useContext(CustomFormContext),
    formDisabled = _React$useContext22.isDisabled,
    formReadonly = _React$useContext22.isReadOnly;
  var isReadOnly = formReadonly || fieldReadonly || props.readOnly;
  var _useField11 = useField(_objectSpread(_objectSpread({
      isDisabled: formDisabled || fieldDisabled,
      readOnly: isReadOnly
    }, props), {}, {
      validateOnBlur: isReadOnly ? false : props.validateOnBlur
    })),
    fieldState = _useField11.fieldState,
    fieldApi = _useField11.fieldApi,
    ref = _useField11.ref,
    render = _useField11.render,
    userProps = _useField11.userProps;
  var handleChange = function handleChange(field, value, e) {
    fieldApi.setValue(value);
    safeInvoke(props.onUpdate, field, value, e);
  };
  var handleBlur = function handleBlur(e) {
    fieldApi.setTouched(true);
    safeInvoke(props.onBlur, e);
  };
  return render(React.createElement(TimeInput, _extends({}, userProps, {
    value: fieldState.value,
    onUpdate: handleChange,
    onBlur: handleBlur
  })));
};
Form.Hidden = function formHidden(props) {
  var _useField12 = useField(props),
    fieldState = _useField12.fieldState,
    fieldApi = _useField12.fieldApi,
    ref = _useField12.ref,
    render = _useField12.render,
    userProps = _useField12.userProps;
  var handleChange = function handleChange(e) {
    var value = e.currentTarget.value;
    fieldApi.setValue(value);
    safeInvoke(props.onChange, e);
  };
  return render(React.createElement(HiddenInput, _extends({
    value: fieldState.value
  }, userProps, {
    onChange: handleChange
  })));
};
export { useField, useFieldApi, useFieldState, useFormApi, useFormState, useMultistepApi, Multistep };